import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
  Download, 
  RefreshCw, 
  Mail, 
  Users, 
  Building2, 
  Search,
  FileJson,
  FileSpreadsheet,
  Eye,
  Loader2,
  LogOut,
  User,
  Package,
  CheckSquare,
  FileText,
  FileDown,
  ChevronDown,
  ChevronRight,
  Plus,
  Ship,
  Import,
  ArrowDownToLine,
  ArrowUpFromLine,
  FolderOpen,
  GitBranch,
  BarChart3,
  Sprout
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminUsername, setAdminUsername] = useState('');
  
  // Navigation state
  const [activeSection, setActiveSection] = useState('contacts');
  const [expandedMenu, setExpandedMenu] = useState({
    directory: false,
    shipments: false
  });
  const [shipmentTypeFilter, setShipmentTypeFilter] = useState('all'); // all, Import, Export
  
  // Data states
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [buyerApplications, setBuyerApplications] = useState([]);
  const [sellerApplications, setSellerApplications] = useState([]);
  const [farmerApplications, setFarmerApplications] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [eximDossiers, setEximDossiers] = useState([]);
  const [eximQaRecords, setEximQaRecords] = useState([]);
  const [eximExportPacks, setEximExportPacks] = useState([]);
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showCreateShipment, setShowCreateShipment] = useState(false);
  const [newShipment, setNewShipment] = useState({
    shipment_type: 'Export',
    product_name: '',
    origin: '',
    destination: '',
    batch_number: '',
    shipment_date: '',
    notes: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        await axios.get(`${API_URL}/api/auth/verify`, getAuthHeaders());
        setIsAuthenticated(true);
        setAdminUsername(localStorage.getItem('adminUsername') || 'Admin');
      } catch (error) {
        console.error('Auth verification failed:', error);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUsername');
        navigate('/admin/login');
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    toast({
      title: "Logged Out",
      description: "You have been successfully logged out",
    });
    navigate('/admin/login');
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const authHeaders = getAuthHeaders();
      const [contacts, buyers, sellers, farmers, shipmentsRes, dossiers, qaRecords, exportPacks] = await Promise.all([
        axios.get(`${API_URL}/api/admin/contacts`, authHeaders),
        axios.get(`${API_URL}/api/admin/buyers`, authHeaders),
        axios.get(`${API_URL}/api/admin/sellers`, authHeaders),
        axios.get(`${API_URL}/api/admin/farmers`, authHeaders).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/eximstack/shipments`, authHeaders).catch(() => ({ data: { shipments: [] } })),
        axios.get(`${API_URL}/api/admin/eximstack/dossiers`, authHeaders).catch(() => ({ data: { dossiers: [] } })),
        axios.get(`${API_URL}/api/admin/eximstack/qa-records`, authHeaders).catch(() => ({ data: { records: [] } })),
        axios.get(`${API_URL}/api/admin/eximstack/export-packs`, authHeaders).catch(() => ({ data: { packs: [] } }))
      ]);
      
      setContactSubmissions(contacts.data || []);
      setBuyerApplications(buyers.data || []);
      setSellerApplications(sellers.data || []);
      setFarmerApplications(farmers.data || []);
      setShipments(shipmentsRes.data?.shipments || []);
      setEximDossiers(dossiers.data?.dossiers || []);
      setEximQaRecords(qaRecords.data?.records || []);
      setEximExportPacks(exportPacks.data?.packs || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUsername');
        navigate('/admin/login');
        return;
      }
      toast({
        title: "Error",
        description: "Failed to load data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleRefresh = async () => {
    await fetchData();
    toast({
      title: "Data Refreshed",
      description: "All data loaded successfully",
    });
  };

  const exportData = async (type, format) => {
    try {
      const isBlob = format === 'excel' || format === 'pdf' || format === 'csv';
      const response = await axios.get(`${API_URL}/api/admin/export/${type}?format=${format}`, {
        ...getAuthHeaders(),
        responseType: isBlob ? 'blob' : 'json'
      });
      
      let blob;
      let extension = format;
      
      if (format === 'csv') {
        blob = new Blob([response.data], { type: 'text/csv' });
      } else if (format === 'excel') {
        blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        extension = 'xlsx';
      } else if (format === 'pdf') {
        blob = new Blob([response.data], { type: 'application/pdf' });
      } else {
        blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast({
        title: "Export Successful",
        description: `${type} data exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive"
      });
    }
  };

  const exportEximData = async (type, format) => {
    try {
      const isBlob = format === 'excel' || format === 'pdf' || format === 'csv';
      const response = await axios.get(
        `${API_URL}/api/admin/eximstack/export/${type}?format=${format}`,
        {
          ...getAuthHeaders(),
          responseType: isBlob ? 'blob' : 'json'
        }
      );
      
      let blob;
      let extension = format;
      
      if (format === 'csv') {
        blob = new Blob([response.data], { type: 'text/csv' });
      } else if (format === 'excel') {
        blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        extension = 'xlsx';
      } else if (format === 'pdf') {
        blob = new Blob([response.data], { type: 'application/pdf' });
      } else {
        blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `eximstack_${type}_export_${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast({
        title: "Export Successful",
        description: `EximStack ${type} data exported as ${format.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Export error:', error);
      toast({
        title: "Export Failed",
        description: "Failed to export data. Please try again.",
        variant: "destructive"
      });
    }
  };

  const createShipment = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/eximstack/shipments`, newShipment);
      toast({
        title: "Shipment Created",
        description: `Batch: ${response.data.shipment.batch_number}`,
      });
      setShowCreateShipment(false);
      setNewShipment({
        shipment_type: 'Export',
        product_name: '',
        origin: '',
        destination: '',
        batch_number: '',
        shipment_date: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create shipment",
        variant: "destructive"
      });
    }
  };

  const migrateData = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/eximstack/migrate-to-shipments`);
      toast({
        title: "Migration Complete",
        description: `Created ${response.data.shipments_created} shipments`,
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Migration Failed",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const filterData = (data) => {
    if (!searchTerm) return data;
    return data.filter(item => 
      JSON.stringify(item).toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'pending_email': 'bg-yellow-100 text-yellow-800',
      'pending_review': 'bg-blue-100 text-blue-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'new': 'bg-purple-100 text-purple-800',
      'Pending': 'bg-yellow-100 text-yellow-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Shipped': 'bg-purple-100 text-purple-800',
      'Delivered': 'bg-green-100 text-green-800',
      'Completed': 'bg-green-100 text-green-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  const viewDetail = (item) => {
    setSelectedItem(item);
    setShowDetail(true);
  };

  const toggleMenu = (menu) => {
    setExpandedMenu(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  // Get filtered shipments by type
  const getFilteredShipments = () => {
    if (shipmentTypeFilter === 'all') return shipments;
    return shipments.filter(s => s.shipment_type === shipmentTypeFilter);
  };

  // Get records for a specific shipment type
  const getRecordsByShipmentType = (records, type) => {
    if (type === 'all') return records;
    const shipmentIds = shipments
      .filter(s => s.shipment_type === type)
      .map(s => s.id);
    return records.filter(r => shipmentIds.includes(r.shipment_id) || !r.shipment_id);
  };

  // Navigation menu items
  const menuItems = [
    {
      id: 'contacts',
      label: 'Contacts',
      icon: Mail,
      count: contactSubmissions.length,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      id: 'directory',
      label: 'Directory',
      icon: FolderOpen,
      expandable: true,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      children: [
        { id: 'buyers', label: 'Buyers', icon: Users, count: buyerApplications.length },
        { id: 'sellers', label: 'Sellers', icon: Building2, count: sellerApplications.length },
        { id: 'farmers', label: 'Partner Farmers', icon: Sprout, count: farmerApplications.length }
      ]
    },
    {
      id: 'shipments',
      label: 'Shipments / Consignments',
      icon: Ship,
      expandable: true,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      children: [
        { id: 'shipments-import', label: 'Import', icon: ArrowDownToLine, 
          count: shipments.filter(s => s.shipment_type === 'Import').length },
        { id: 'shipments-export', label: 'Export', icon: ArrowUpFromLine, 
          count: shipments.filter(s => s.shipment_type === 'Export').length }
      ]
    }
  ];

  // Export buttons component
  const ExportButtons = ({ type, isExim = false }) => (
    <div className="flex gap-2 flex-wrap">
      <Button size="sm" variant="outline" onClick={() => isExim ? exportEximData(type, 'csv') : exportData(type, 'csv')}>
        <FileSpreadsheet className="h-4 w-4 mr-1" /> CSV
      </Button>
      <Button size="sm" variant="outline" onClick={() => isExim ? exportEximData(type, 'excel') : exportData(type, 'excel')}>
        <FileDown className="h-4 w-4 mr-1" /> Excel
      </Button>
      <Button size="sm" variant="outline" onClick={() => isExim ? exportEximData(type, 'pdf') : exportData(type, 'pdf')}>
        <Download className="h-4 w-4 mr-1" /> PDF
      </Button>
      <Button size="sm" variant="outline" onClick={() => isExim ? exportEximData(type, 'json') : exportData(type, 'json')}>
        <FileJson className="h-4 w-4 mr-1" /> JSON
      </Button>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="w-full min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#263776]" />
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#263776] text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Admin Dashboard</h1>
              <p className="text-slate-300 mt-2">Manage submissions, directory, and shipments</p>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/analytics">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-[#F47D20] text-[#F47D20] hover:bg-[#F47D20] hover:text-white bg-transparent"
                  data-testid="analytics-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Analytics
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-slate-300">
                <User className="w-4 h-4" />
                <span className="text-sm">{adminUsername}</span>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="border-white text-white hover:bg-white/20 bg-transparent"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="container mx-auto px-4 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Card className="shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Contacts</CardTitle>
              <Mail className="h-5 w-5 text-[#F47D20]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#263776]">{contactSubmissions.length}</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Buyers</CardTitle>
              <Users className="h-5 w-5 text-[#F47D20]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#263776]">{buyerApplications.length}</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-600">Sellers</CardTitle>
              <Building2 className="h-5 w-5 text-[#F47D20]" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-[#263776]">{sellerApplications.length}</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg bg-green-50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-green-700">Partner Farmers</CardTitle>
              <Sprout className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-700">{farmerApplications.length}</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg bg-purple-50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-purple-700">Shipments</CardTitle>
              <Ship className="h-5 w-5 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-700">{shipments.length}</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg bg-green-50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-green-700">Dossiers</CardTitle>
              <FileText className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-700">{eximDossiers.length}</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg bg-blue-50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-blue-700">QA Records</CardTitle>
              <CheckSquare className="h-5 w-5 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-700">{eximQaRecords.length}</div>
            </CardContent>
          </Card>
          <Card className="shadow-lg bg-orange-50">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-orange-700">Export Packs</CardTitle>
              <Package className="h-5 w-5 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-700">{eximExportPacks.length}</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Navigation</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <nav className="space-y-1">
                  {menuItems.map((item) => (
                    <div key={item.id}>
                      <button
                        onClick={() => item.expandable ? toggleMenu(item.id) : setActiveSection(item.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                          activeSection === item.id || (item.expandable && expandedMenu[item.id])
                            ? `${item.bgColor} ${item.color}`
                            : 'hover:bg-gray-100'
                        }`}
                        data-testid={`nav-${item.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <item.icon className="w-4 h-4" />
                          <span className="text-sm font-medium">{item.label}</span>
                          {item.count !== undefined && (
                            <Badge variant="secondary" className="text-xs">{item.count}</Badge>
                          )}
                        </div>
                        {item.expandable && (
                          expandedMenu[item.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                      
                      {item.expandable && expandedMenu[item.id] && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map((child) => (
                            <button
                              key={child.id}
                              onClick={() => {
                                setActiveSection(child.id);
                                if (child.id.startsWith('shipments-')) {
                                  setShipmentTypeFilter(child.id === 'shipments-import' ? 'Import' : 'Export');
                                }
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors ${
                                activeSection === child.id
                                  ? 'bg-gray-100 text-gray-900'
                                  : 'hover:bg-gray-50'
                              }`}
                              data-testid={`nav-${child.id}`}
                            >
                              <div className="flex items-center gap-2">
                                <child.icon className="w-4 h-4" />
                                <span className="text-sm">{child.label}</span>
                              </div>
                              <Badge variant="secondary" className="text-xs">{child.count}</Badge>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </nav>
                
                {/* Quick Actions */}
                <div className="mt-6 pt-4 border-t">
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2 px-2">Quick Actions</p>
                  <div className="space-y-2">
                    <Button 
                      size="sm" 
                      className="w-full justify-start bg-green-600 hover:bg-green-700"
                      onClick={() => setShowCreateShipment(true)}
                      data-testid="create-shipment-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Shipment
                    </Button>
                    <Link to="/eximstack/traceability/new" className="block">
                      <Button size="sm" variant="outline" className="w-full justify-start">
                        <FileText className="w-4 h-4 mr-2" />
                        Create Dossier
                      </Button>
                    </Link>
                    <Link to="/eximstack/qa-gates/new" className="block">
                      <Button size="sm" variant="outline" className="w-full justify-start">
                        <CheckSquare className="w-4 h-4 mr-2" />
                        Create QA Record
                      </Button>
                    </Link>
                    <Link to="/eximstack/export-pack/new" className="block">
                      <Button size="sm" variant="outline" className="w-full justify-start">
                        <Package className="w-4 h-4 mr-2" />
                        New Export Pack
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            <Card className="shadow-lg">
              <CardHeader>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Button onClick={handleRefresh} variant="outline" disabled={loading}>
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Contacts Section */}
                {activeSection === 'contacts' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Mail className="h-5 w-5 text-orange-600" /> Contact Submissions
                      </h3>
                      <ExportButtons type="contacts" />
                    </div>
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Request Type</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filterData(contactSubmissions).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                                No contact submissions found
                              </TableCell>
                            </TableRow>
                          ) : (
                            filterData(contactSubmissions).map((item, index) => (
                              <TableRow key={index}>
                                <TableCell className="text-sm">{formatDate(item.submittedAt)}</TableCell>
                                <TableCell className="font-medium">{item.name}</TableCell>
                                <TableCell>{item.company}</TableCell>
                                <TableCell>{item.email}</TableCell>
                                <TableCell>{item.requestType}</TableCell>
                                <TableCell>
                                  <Badge className={getStatusBadge(item.status)}>{item.status}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Button size="sm" variant="ghost" onClick={() => viewDetail(item)}>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Buyers Section */}
                {activeSection === 'buyers' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Users className="h-5 w-5 text-blue-600" /> Buyer Applications
                      </h3>
                      <ExportButtons type="buyers" />
                    </div>
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Country</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filterData(buyerApplications).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                                No buyer applications found
                              </TableCell>
                            </TableRow>
                          ) : (
                            filterData(buyerApplications).map((item, index) => (
                              <TableRow key={index}>
                                <TableCell className="text-sm">{formatDate(item.submittedAt)}</TableCell>
                                <TableCell className="font-medium">{item.companyName}</TableCell>
                                <TableCell>{item.contactName}</TableCell>
                                <TableCell>{item.country}</TableCell>
                                <TableCell>{item.email}</TableCell>
                                <TableCell>
                                  <Badge className={getStatusBadge(item.status)}>{item.status}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Button size="sm" variant="ghost" onClick={() => viewDetail(item)}>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Sellers Section */}
                {activeSection === 'sellers' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-purple-600" /> Seller Applications
                      </h3>
                      <ExportButtons type="sellers" />
                    </div>
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Company</TableHead>
                            <TableHead>Contact</TableHead>
                            <TableHead>Country</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filterData(sellerApplications).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                                No seller applications found
                              </TableCell>
                            </TableRow>
                          ) : (
                            filterData(sellerApplications).map((item, index) => (
                              <TableRow key={index}>
                                <TableCell className="text-sm">{formatDate(item.submittedAt)}</TableCell>
                                <TableCell className="font-medium">{item.companyName}</TableCell>
                                <TableCell>{item.contactName}</TableCell>
                                <TableCell>{item.country}</TableCell>
                                <TableCell>{item.email}</TableCell>
                                <TableCell>
                                  <Badge className={getStatusBadge(item.status)}>{item.status}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Button size="sm" variant="ghost" onClick={() => viewDetail(item)}>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Farmers Section */}
                {activeSection === 'farmers' && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Sprout className="h-5 w-5 text-green-600" /> Partner Farmer Applications
                      </h3>
                      <ExportButtons type="farmers" />
                    </div>
                    
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Farm Name</TableHead>
                            <TableHead>Farmer</TableHead>
                            <TableHead>Location</TableHead>
                            <TableHead>Crops</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filterData(farmerApplications).length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                                No farmer applications found
                              </TableCell>
                            </TableRow>
                          ) : (
                            filterData(farmerApplications).map((item, index) => (
                              <TableRow key={index}>
                                <TableCell className="text-sm">{formatDate(item.submittedAt)}</TableCell>
                                <TableCell className="font-medium">{item.farmName}</TableCell>
                                <TableCell>{item.farmerName}</TableCell>
                                <TableCell>{item.district}, {item.state}</TableCell>
                                <TableCell className="max-w-[150px] truncate">
                                  {item.primaryCrops?.join(', ') || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <Badge className={getStatusBadge(item.status)}>{item.status}</Badge>
                                </TableCell>
                                <TableCell>
                                  <Button size="sm" variant="ghost" onClick={() => viewDetail(item)}>
                                    <Eye className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {/* Shipments Section - Import or Export */}
                {(activeSection === 'shipments-import' || activeSection === 'shipments-export') && (
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        {shipmentTypeFilter === 'Import' ? (
                          <><ArrowDownToLine className="h-5 w-5 text-blue-600" /> Import Shipments</>
                        ) : (
                          <><ArrowUpFromLine className="h-5 w-5 text-green-600" /> Export Shipments</>
                        )}
                      </h3>
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          setNewShipment(prev => ({ ...prev, shipment_type: shipmentTypeFilter }));
                          setShowCreateShipment(true);
                        }}
                      >
                        <Plus className="h-4 w-4 mr-1" /> New {shipmentTypeFilter}
                      </Button>
                    </div>
                    
                    <div className="rounded-md border overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Batch Number</TableHead>
                            <TableHead>Product</TableHead>
                            <TableHead>Route</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Records</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getFilteredShipments().length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                                No {shipmentTypeFilter.toLowerCase()} shipments found
                              </TableCell>
                            </TableRow>
                          ) : (
                            getFilteredShipments().map((shipment, index) => {
                              const relatedDossiers = eximDossiers.filter(d => d.shipment_id === shipment.id).length;
                              const relatedQA = eximQaRecords.filter(q => q.shipment_id === shipment.id).length;
                              const relatedPacks = eximExportPacks.filter(p => p.shipment_id === shipment.id).length;
                              
                              return (
                                <TableRow key={index}>
                                  <TableCell className="text-sm">{formatDate(shipment.created_at)}</TableCell>
                                  <TableCell className="font-mono text-sm">{shipment.batch_number}</TableCell>
                                  <TableCell className="font-medium">{shipment.product_name}</TableCell>
                                  <TableCell className="text-sm">
                                    {shipment.origin} → {shipment.destination}
                                  </TableCell>
                                  <TableCell>
                                    <Badge className={getStatusBadge(shipment.status)}>{shipment.status}</Badge>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      <Badge variant="outline" className="text-xs">D: {relatedDossiers}</Badge>
                                      <Badge variant="outline" className="text-xs">QA: {relatedQA}</Badge>
                                      <Badge variant="outline" className="text-xs">EP: {relatedPacks}</Badge>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      <Link to={`/shipment/${shipment.id}`}>
                                        <Button size="sm" variant="outline" className="text-purple-600 border-purple-300 hover:bg-purple-50" data-testid={`timeline-btn-${shipment.id}`}>
                                          <GitBranch className="h-4 w-4 mr-1" />
                                          Timeline
                                        </Button>
                                      </Link>
                                      <Button size="sm" variant="ghost" onClick={() => viewDetail(shipment)}>
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Related Records */}
                    {getFilteredShipments().length > 0 && (
                      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Dossiers for this type */}
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <FileText className="h-4 w-4 text-green-600" />
                              Dossiers ({getRecordsByShipmentType(eximDossiers, shipmentTypeFilter).length})
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="max-h-40 overflow-y-auto">
                            {getRecordsByShipmentType(eximDossiers, shipmentTypeFilter).slice(0, 5).map((d, i) => (
                              <div key={i} className="text-sm py-1 border-b last:border-0">
                                <p className="font-medium truncate">{d.product_name}</p>
                                <p className="text-xs text-gray-500">{d.batch_id}</p>
                              </div>
                            ))}
                            {getRecordsByShipmentType(eximDossiers, shipmentTypeFilter).length === 0 && (
                              <p className="text-sm text-gray-500">No dossiers</p>
                            )}
                          </CardContent>
                        </Card>
                        
                        {/* QA Records for this type */}
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <CheckSquare className="h-4 w-4 text-blue-600" />
                              QA Records ({getRecordsByShipmentType(eximQaRecords, shipmentTypeFilter).length})
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="max-h-40 overflow-y-auto">
                            {getRecordsByShipmentType(eximQaRecords, shipmentTypeFilter).slice(0, 5).map((r, i) => (
                              <div key={i} className="text-sm py-1 border-b last:border-0">
                                <p className="font-medium truncate">{r.product_name}</p>
                                <Badge className={getStatusBadge(r.overall_status)} variant="secondary">
                                  {r.overall_status}
                                </Badge>
                              </div>
                            ))}
                            {getRecordsByShipmentType(eximQaRecords, shipmentTypeFilter).length === 0 && (
                              <p className="text-sm text-gray-500">No QA records</p>
                            )}
                          </CardContent>
                        </Card>
                        
                        {/* Export Packs for this type */}
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <Package className="h-4 w-4 text-orange-600" />
                              Export Packs ({getRecordsByShipmentType(eximExportPacks, shipmentTypeFilter).length})
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="max-h-40 overflow-y-auto">
                            {getRecordsByShipmentType(eximExportPacks, shipmentTypeFilter).slice(0, 5).map((p, i) => (
                              <div key={i} className="text-sm py-1 border-b last:border-0">
                                <p className="font-medium truncate">{p.product_name}</p>
                                <div className="flex items-center gap-2">
                                  <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                                    <div className="h-full bg-orange-500 rounded-full" style={{ width: `${p.completion_percentage}%` }}></div>
                                  </div>
                                  <span className="text-xs">{p.completion_percentage}%</span>
                                </div>
                              </div>
                            ))}
                            {getRecordsByShipmentType(eximExportPacks, shipmentTypeFilter).length === 0 && (
                              <p className="text-sm text-gray-500">No export packs</p>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Create Shipment Modal */}
      {showCreateShipment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ship className="h-5 w-5 text-green-600" />
                Create New Shipment
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Type *</label>
                  <select 
                    className="w-full border rounded-md p-2 mt-1"
                    value={newShipment.shipment_type}
                    onChange={(e) => setNewShipment({ ...newShipment, shipment_type: e.target.value })}
                  >
                    <option value="Export">Export</option>
                    <option value="Import">Import</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">Product Name *</label>
                  <Input 
                    value={newShipment.product_name}
                    onChange={(e) => setNewShipment({ ...newShipment, product_name: e.target.value })}
                    placeholder="e.g., Premium Turmeric Fingers"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Origin *</label>
                    <Input 
                      value={newShipment.origin}
                      onChange={(e) => setNewShipment({ ...newShipment, origin: e.target.value })}
                      placeholder="e.g., India"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Destination *</label>
                    <Input 
                      value={newShipment.destination}
                      onChange={(e) => setNewShipment({ ...newShipment, destination: e.target.value })}
                      placeholder="e.g., UAE"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Batch Number (Optional)</label>
                    <Input 
                      value={newShipment.batch_number}
                      onChange={(e) => setNewShipment({ ...newShipment, batch_number: e.target.value })}
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Shipment Date</label>
                    <Input 
                      type="date"
                      value={newShipment.shipment_date}
                      onChange={(e) => setNewShipment({ ...newShipment, shipment_date: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Notes</label>
                  <textarea 
                    className="w-full border rounded-md p-2 mt-1"
                    rows={2}
                    value={newShipment.notes}
                    onChange={(e) => setNewShipment({ ...newShipment, notes: e.target.value })}
                    placeholder="Any additional notes..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => setShowCreateShipment(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={createShipment}
                  disabled={!newShipment.product_name || !newShipment.origin || !newShipment.destination}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Shipment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Details</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowDetail(false)}>
                Close
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(selectedItem).map(([key, value]) => {
                  if (key === '_id') return null;
                  return (
                    <div key={key} className="border-b pb-2">
                      <span className="text-sm font-medium text-slate-500 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').trim()}:
                      </span>
                      <p className="text-slate-900 mt-1">
                        {typeof value === 'object' 
                          ? JSON.stringify(value, null, 2)
                          : Array.isArray(value) 
                            ? value.join(', ') 
                            : String(value || 'N/A')}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
